import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatApiErrorDetail } from "@/components/auth/PasswordSignIn";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError("The two passwords do not match."); return; }
    setSubmitting(true); setError(null);
    try {
      const { data } = await api.post("/auth/password/reset", { token, password });
      toast.success(data.message);
      navigate("/login?mode=password");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail, "This link is invalid, has expired or has already been used."));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="set-password-page" className="flex min-h-screen items-center justify-center bg-[#F6F8FB] px-4 py-8">
      <div className="w-full max-w-[520px] rounded-2xl bg-white p-8 shadow-[0_12px_28px_-4px_rgba(16,24,47,0.14)] md:p-12">
        <h1 className="font-display text-[32px] font-normal leading-tight text-jigsaw-navy">Choose your password</h1>
        <p className="mt-2 text-base text-jigsaw-muted">At least 12 characters, not based on your e-mail address. Saving signs you out of every other session; your sign-in code option stays available.</p>
        {!token ? (
          <p data-testid="set-password-no-token" role="alert" className="mt-6 rounded-lg border border-jigsaw-border bg-[#F6F8FB] px-4 py-3 text-sm text-jigsaw-navy">
            This page needs the link from your e-mail. <Link className="underline" to="/login?mode=password">Request a new link</Link>.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
            <div className="space-y-2">
              <label htmlFor="new-password" className="text-sm font-medium text-jigsaw-navy">New password</label>
              <Input id="new-password" data-testid="new-password-input" type="password" autoComplete="new-password" required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-lg border-jigsaw-border text-[15px]" />
            </div>
            <div className="space-y-2">
              <label htmlFor="confirm-password" className="text-sm font-medium text-jigsaw-navy">Confirm password</label>
              <Input id="confirm-password" data-testid="confirm-password-input" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12 rounded-lg border-jigsaw-border text-[15px]" />
            </div>
            {error && <p data-testid="set-password-error" role="alert" className="text-sm text-jigsaw-danger">{error}</p>}
            <Button data-testid="set-password-submit" type="submit" disabled={submitting || password.length < 12 || !confirm} className="h-12 w-full rounded-lg bg-jigsaw-orange text-[15px] font-semibold text-white hover:bg-jigsaw-orangeDark disabled:bg-jigsaw-orange/40 disabled:text-white">
              {submitting ? "Saving..." : "Save password"}
            </Button>
            <Link data-testid="set-password-back" className="block text-sm text-jigsaw-muted underline-offset-4 hover:underline" to="/login">Back to sign in</Link>
          </form>
        )}
      </div>
    </div>
  );
}
